import { useEffect, useRef, useState } from 'react';
import { Check, Minus, MoreVertical, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { FONT_BODY, FONT_MONO } from './theme';
import { resolveDone, rirLabel, stepValue } from './setLog';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Set rows of the Today tab                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

/* Set | kg | reps | done. No remove column: removing lives in the set menu,
 * away from Done, with undo. */
export const SET_GRID = '34px minmax(0, 1fr) minmax(0, 1fr) 56px';

/* Stored rir values; 5 means "4+" (docs/fitness-data-model.md). */
export const RIR_CHOICES = [0, 1, 2, 3, 5];

const fmtNum = v => (typeof v === 'number' && Number.isFinite(v) ? String(Math.round(v * 100) / 100) : '');
const toNum = v => (v === '' ? null : Number(v));

/* Long-press without a library: a timer between pointerdown and pointerup,
 * cancelled when the finger moves (a scroll) or lifts early. */
function useLongPress(onLongPress, delay = 450) {
  const timer = useRef(null);
  const start = useRef(null);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const cancel = () => { window.clearTimeout(timer.current); timer.current = null; };
  return {
    onPointerDown: e => {
      cancel();
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        timer.current = null;
        try { if (navigator.vibrate) navigator.vibrate(10); } catch {}
        onLongPress();
      }, delay);
    },
    onPointerMove: e => {
      if (!timer.current || !start.current) return;
      if (Math.abs(e.clientX - start.current.x) > 8 || Math.abs(e.clientY - start.current.y) > 8) cancel();
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onContextMenu: e => e.preventDefault(),
  };
}

/* The set number opens the set menu (RIR, remove) on tap or long-press;
 * the dots say so. A logged RIR shows as a badge under it. */
export function SetNumber({ t, n, done, rir }) {
  const label = rirLabel(rir);
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pointerEvents: 'none' }}>
      <span style={{ display: 'flex', alignItems: 'center', fontSize: 12, fontFamily: FONT_MONO, fontWeight: 700, color: done ? t.accent : t.textMuted }}>
        {n}<MoreVertical size={10} color={t.textFaint} />
      </span>
      {label && (
        <span title={`RIR ${label}`} style={{
          fontSize: 9, fontFamily: FONT_MONO, fontWeight: 700, lineHeight: 1, padding: '2px 3px', borderRadius: 4,
          background: t.accentSoft, color: t.accent,
        }}>R{label}</span>
      )}
    </span>
  );
}

function SetNumberButton({ t, n, done, rir, onMenu }) {
  const press = useLongPress(onMenu);
  return (
    <button {...press} onClick={onMenu} aria-label={`Set ${n} options`} title="Set options: RIR, remove"
      style={{
        height: 40, width: '100%', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'manipulation',
      }}>
      <SetNumber t={t} n={n} done={done} rir={rir} />
    </button>
  );
}

/* Compact cell input. `auto` marks reps that Done filled in from the ghost,
 * so Mo sees they were not typed and can tap to correct them. */
function CellInput({ t, value, placeholder, onChange, inputRef, auto, label, numeric }) {
  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <input ref={inputRef} type="number" inputMode={numeric ? 'numeric' : 'decimal'} step="any" min={0}
        aria-label={label}
        value={value ?? ''}
        placeholder={placeholder != null ? String(placeholder) : undefined}
        onChange={e => onChange(toNum(e.target.value))}
        style={{
          width: '100%', height: 40, background: t.inputBg, color: auto ? t.textMuted : t.text,
          fontFamily: FONT_MONO, fontSize: 14, fontWeight: 600, fontStyle: auto ? 'italic' : 'normal',
          border: `1px ${auto ? 'dashed' : 'solid'} ${auto ? t.accentBorder : t.inputBorder}`, borderRadius: 8,
          padding: '0 4px', outline: 'none', textAlign: 'center', transition: 'color 0.3s, border-color 0.3s',
        }} />
      {auto && (
        <span style={{
          position: 'absolute', top: 3, right: 6, fontSize: 8, fontWeight: 700, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: t.accent, pointerEvents: 'none',
        }}>auto</span>
      )}
    </div>
  );
}

export function SetTableHeader({ t }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: SET_GRID, gap: 6, padding: '12px 0 6px',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.textFaint,
    }}>
      <span style={{ textAlign: 'center' }}>Set</span>
      <span style={{ textAlign: 'center' }}>kg</span>
      <span style={{ textAlign: 'center' }}>Reps</span>
      <span style={{ textAlign: 'center' }}>Done</span>
    </div>
  );
}

/* Reps placeholder: "5+" on an AMRAP target set (5 is the minimum). */
const repsGhost = ghost => (ghost.amrap && ghost.reps != null ? `${ghost.reps}+` : ghost.reps);

/* A set that is not the one being lifted now: done, or further down. */
export function CompactSetRow({ t, n, set, ghost, autoReps, onWeight, onReps, onToggle, onMenu, weightRef, repsRef, first, active, id }) {
  const done = !!set.done;
  // active: the compact template's next set to do, highlighted with a Done button.
  const live = active && !done;
  return (
    <div id={id} style={{
      display: 'grid', gridTemplateColumns: SET_GRID, gap: 6, padding: live ? '6px' : '4px 0', alignItems: 'center',
      borderTop: first || live ? 'none' : `1px solid ${t.divider}`,
      ...(live ? { background: t.accentSoft, borderRadius: 12, margin: '4px -6px' } : {}),
    }}>
      <SetNumberButton t={t} n={n} done={done} rir={set.rir} onMenu={onMenu} />
      <CellInput t={t} label={`Set ${n} kg`} value={set.weight ?? null} placeholder={ghost.weight}
        onChange={onWeight} inputRef={weightRef} />
      <CellInput t={t} label={`Set ${n} reps`} numeric value={set.reps ?? null} placeholder={repsGhost(ghost)}
        onChange={onReps} inputRef={repsRef} auto={autoReps} />
      <button onClick={onToggle} aria-label={done ? `Untick set ${n}` : `Tick set ${n} done`} style={{
        height: live ? 44 : 40, borderRadius: live ? 10 : 8, cursor: 'pointer',
        border: `1px solid ${done || live ? t.accent : t.cardBorder === 'transparent' ? t.divider : t.cardBorder}`,
        background: done || live ? t.accent : 'transparent', color: done || live ? '#fff' : t.textMuted,
        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
        fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700,
      }}>
        {live ? 'Done' : <Check size={16} />}
      </button>
    </div>
  );
}

function Stepper({ t, unit, hint, value, ghost, ghostText, step, min = 0, onChange, inputRef, numeric }) {
  const [focused, setFocused] = useState(false); // Done may focus an empty field; make that visible
  const base = typeof value === 'number' ? value : (typeof ghost === 'number' ? ghost : null);
  const btn = {
    border: 'none', background: 'transparent', color: t.text, cursor: 'pointer', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'manipulation',
  };
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr) 40px', height: 48, minWidth: 0,
      border: `1px solid ${focused ? t.accent : t.inputBorder}`, borderRadius: 10, background: t.inputBg, overflow: 'hidden',
    }}>
      <button aria-label={`Less ${unit}`} onClick={() => onChange(stepValue(base, -step, min))} style={btn}>
        <Minus size={16} />
      </button>
      <div style={{ position: 'relative', minWidth: 0 }}>
        <input ref={inputRef} type="number" inputMode={numeric ? 'numeric' : 'decimal'} step="any" min={min}
          aria-label={unit}
          value={value ?? ''}
          placeholder={ghostText != null ? String(ghostText) : ghost != null ? String(ghost) : undefined}
          onChange={e => onChange(toNum(e.target.value))}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{
            width: '100%', height: '100%', border: 'none', background: 'transparent', color: t.text,
            fontFamily: FONT_MONO, fontSize: 16, fontWeight: 700, textAlign: 'center', outline: 'none',
            padding: '0 2px 11px',
          }} />
        <span style={{
          position: 'absolute', left: 0, right: 0, bottom: 5, textAlign: 'center', pointerEvents: 'none',
          fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', color: t.textFaint, whiteSpace: 'nowrap',
        }}>{unit.toUpperCase()}{hint ? ` ${hint}` : ''}</span>
      </div>
      <button aria-label={`More ${unit}`} onClick={() => onChange(stepValue(base, step, min))} style={btn}>
        <Plus size={16} />
      </button>
    </div>
  );
}

/* The first unfinished set, opened up for one-handed use: kg and reps
 * steppers and a full-width Done that says what it will save. */
export function ActiveSetPanel({ t, id, n, set, ghost, kgStep, onWeight, onReps, onDone, onMenu, weightRef, repsRef }) {
  const r = resolveDone(set, ghost);
  // An empty AMRAP set: Done asks for the reps, so show the minimum as "5+".
  const plus = ghost.amrap && (set.reps == null || set.reps === '') ? '+' : '';
  const summary = r.reps === null ? ''
    : r.weight === null ? `${r.reps}${plus} reps`
    : `${fmtNum(r.weight)} × ${r.reps}${plus}`;
  return (
    <div id={id} style={{
      margin: '6px 0', padding: 8, borderRadius: 12, background: t.accentSoft,
      border: `1px solid ${t.accentBorder}`, display: 'flex', flexDirection: 'column', gap: 8,
      // Scrolled into view after a tick: clear of the sticky header and the floating rest timer.
      scrollMarginTop: 112, scrollMarginBottom: 100,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr) minmax(0, 1fr)', gap: 6, alignItems: 'center' }}>
        <SetNumberButton t={t} n={n} done={false} rir={set.rir} onMenu={onMenu} />
        <Stepper t={t} unit="kg" hint={`±${fmtNum(kgStep)}`} value={set.weight ?? null} ghost={ghost.weight}
          step={kgStep} onChange={onWeight} inputRef={weightRef} />
        <Stepper t={t} unit="reps" hint={ghost.amrap ? 'AMRAP' : ''} numeric value={set.reps ?? null} ghost={ghost.reps}
          ghostText={repsGhost(ghost)} step={1} min={1} onChange={onReps} inputRef={repsRef} />
      </div>
      <button onClick={onDone} style={{
        width: '100%', height: 48, borderRadius: 10, border: 'none', cursor: 'pointer',
        background: t.accent, color: '#fff', fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, touchAction: 'manipulation',
      }}>
        <Check size={18} /> Done
        {summary && <span style={{ fontFamily: FONT_MONO, fontWeight: 600, opacity: 0.85 }}>· {summary}</span>}
      </button>
    </div>
  );
}

/* RIR choices; tapping the chosen one again clears it. A stored 4 (e.g.
 * from Claude) lights up "4+". */
export function RirChips({ t, value, onPick, disabled, height = 40 }) {
  const has = typeof value === 'number';
  return (
    <div style={{ display: 'flex', gap: 6, flex: 1, minWidth: 0 }}>
      {RIR_CHOICES.map(v => {
        const on = has && (v === 5 ? value >= 4 : value === v);
        return (
          <button key={v} disabled={disabled} aria-pressed={on} onClick={() => onPick(on ? null : v)} style={{
            flex: 1, minWidth: 0, height, borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
            border: `1px solid ${on ? t.accent : t.cardBorder}`, background: on ? t.accent : t.surfaceAlt,
            color: on ? '#fff' : t.text, fontFamily: FONT_MONO, fontSize: 14, fontWeight: 700,
            opacity: disabled ? 0.4 : 1, touchAction: 'manipulation',
          }}>
            {v === 5 ? '4+' : v}
          </button>
        );
      })}
    </div>
  );
}

/* Shown after the last planned set of an exercise, until the next tick. */
export function RirStrip({ t, caption, value, onPick, onClose }) {
  return (
    <div style={{ padding: '6px 0 10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6, fontSize: 11 }}>
        <span style={{ color: t.textMuted, fontFamily: FONT_MONO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {caption}
        </span>
        <span style={{ color: t.textFaint, flexShrink: 0 }}>reps left · 0 = failure</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: t.textFaint, width: 26 }}>RIR</span>
        <RirChips t={t} value={value} onPick={onPick} />
        <button onClick={onClose} aria-label="Close RIR" style={{
          width: 40, height: 40, flexShrink: 0, border: 'none', background: 'transparent', color: t.textFaint,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/* Bottom sheet for one set: RIR and Remove. */
export function SetMenuSheet({ t, n, name, set, onRir, onRemove, onClose }) {
  // The finger that long-pressed is still down when the sheet appears; a
  // backdrop tap within the first moment is that same press, not a dismiss.
  const openedAt = useRef(Date.now());
  const dismiss = () => { if (Date.now() - openedAt.current > 400) onClose(); };
  const done = !!set?.done;
  const values = [
    set?.weight != null && set.weight !== '' ? `${set.weight} kg` : null,
    set?.reps != null && set.reps !== '' ? `${set.reps} reps` : null,
  ].filter(Boolean).join(' × ');
  return (
    <div onClick={dismiss} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div role="dialog" aria-label={`Set ${n} options`} onClick={e => e.stopPropagation()} style={{
        background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560,
        padding: '14px 16px max(16px, env(safe-area-inset-bottom))', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)',
        display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT_BODY,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>
              Set {n}
              {values && <span style={{ marginLeft: 8, fontSize: 13, fontFamily: FONT_MONO, fontWeight: 600, color: t.textMuted }}>{values}</span>}
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            width: 40, height: 40, flexShrink: 0, border: 'none', background: 'transparent', color: t.textFaint,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={18} />
          </button>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textFaint }}>
              Reps in reserve
            </span>
            <span style={{ fontSize: 11, color: t.textFaint }}>
              {done ? '0 = failure · 4+ = easy' : 'Tick the set first'}
            </span>
          </div>
          <RirChips t={t} value={set?.rir} onPick={onRir} disabled={!done} height={44} />
        </div>
        <button onClick={onRemove} style={{
          width: '100%', height: 44, borderRadius: 10, cursor: 'pointer', border: `1px solid ${t.dangerSoft}`,
          background: t.dangerSoft, color: t.danger, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <Trash2 size={15} /> Remove set
        </button>
      </div>
    </div>
  );
}

export function UndoSnackbar({ t, text, onUndo, bottom = 24 }) {
  return (
    <div role="status" style={{
      // --fit-bottom: the compact template's tab bar.
      position: 'fixed', bottom: `calc(${typeof bottom === 'number' ? `${bottom}px` : bottom} + var(--fit-bottom, 0px))`,
      left: '50%', transform: 'translateX(-50%)', zIndex: 60,
      maxWidth: 'calc(100vw - 32px)', display: 'flex', alignItems: 'center', gap: 4,
      padding: '4px 4px 4px 16px', borderRadius: 12, background: t.surface, color: t.text,
      border: `1px solid ${t.cardBorder}`, boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
      fontFamily: FONT_BODY, fontSize: 13, whiteSpace: 'nowrap',
    }}>
      <span>{text} ·</span>
      <button onClick={onUndo} style={{
        height: 40, padding: '0 12px', border: 'none', background: 'transparent', color: t.accent,
        fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <RotateCcw size={14} /> Undo
      </button>
    </div>
  );
}
